import {
  ApiError,
  activateZone,
  deactivateZone,
  getLocation,
  listZones,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { LocationResponse, ZoneResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { Link, useParams } from '@tanstack/react-router';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { ActivateZoneDialog } from '../components/ActivateZoneDialog';
import { DeactivateZoneDialog } from '../components/DeactivateZoneDialog';
import { ZonesTable } from '../components/ZonesTable';

export function ZonesIndexPage() {
  const { t } = useTranslation('zones');
  const { locationId } = useParams({ strict: false });

  const [zones, setZones] = useState<ZoneResponse[]>([]);
  const [location, setLocation] = useState<LocationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // zones", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<ZoneResponse | null>(null);
  const [activateTarget, setActivateTarget] = useState<ZoneResponse | null>(null);

  const reload = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const [zonesRes, locationRes] = await Promise.all([
        listZones(locationId),
        getLocation(locationId),
      ]);
      setZones(zonesRes.zones);
      setLocation(locationRes);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('page.index.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleDeactivateConfirm() {
    if (!locationId || !deactivateTarget) return;
    try {
      await deactivateZone(locationId, deactivateTarget.id);
      setSuccess(t('feedback.deactivatedSuccess', { name: deactivateTarget.name }));
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    }
  }

  async function handleActivateConfirm() {
    if (!locationId || !activateTarget) return;
    try {
      await activateZone(locationId, activateTarget.id);
      setSuccess(t('feedback.activatedSuccess', { name: activateTarget.name }));
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={
          location
            ? t('page.index.titleWithLocation', { name: location.name })
            : t('page.index.title')
        }
        description={t('page.index.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && zones.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{zones.length}</span>{' '}
              {t('unit', { count: zones.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Link
            to="/dashboard/locations/$locationId/zones/new"
            params={{ locationId: locationId ?? '' }}
          >
            <Button size="sm">
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              {t('page.index.newZone')}
            </Button>
          </Link>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <CapacitySummary location={location} zones={zones} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <ZonesTable
              zones={zones}
              locationId={locationId ?? ''}
              loading={loading}
              onDeactivate={setDeactivateTarget}
              onActivate={setActivateTarget}
            />
          )}
        </CardContent>
      </Card>

      <DeactivateZoneDialog
        zone={deactivateTarget}
        onConfirm={handleDeactivateConfirm}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ActivateZoneDialog
        zone={activateTarget}
        onConfirm={handleActivateConfirm}
        onCancel={() => setActivateTarget(null)}
      />
    </div>
  );
}

// ── Capacity summary ───────────────────────────────────────────────────────
// location-zone-capacity-guard: both `location` and `zones` are already
// loaded by reload() above, so this is computed client-side with no extra
// request. Only rendered when the location has a finite vehicleCapacity —
// mirrors the backend semantics (active zones only, null capacity = 0).

function CapacitySummary({
  location,
  zones,
}: {
  location: LocationResponse | null;
  zones: ZoneResponse[];
}) {
  const { t } = useTranslation('zones');
  if (!location || location.vehicleCapacity === null) return null;

  const total = location.vehicleCapacity;
  const assigned = zones
    .filter((z) => z.status === 'active')
    .reduce((sum, z) => sum + (z.capacity ?? 0), 0);
  const remaining = total - assigned;

  return (
    <p className="text-[13px] text-jq-graphite">
      {remaining < 0
        ? t('capacitySummary.over', { assigned, total, over: Math.abs(remaining) })
        : t('capacitySummary.remaining', { assigned, total, remaining })}
    </p>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('zones');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
